import styled, { css } from "styled-components/macro";
import { rgba } from "polished";

import {
  colors,
  space,
  borderRadius,
  fontStyle,
  fontSize,
  boxShadow,
  transition,
  breakpoint,
} from "shared/styles/constants";
import { hoverFocus } from "shared/styles/defaults";

export interface TeaserContainerProps {
  isInteractive?: boolean;
  hasHorizontalContent?: boolean;
}

const TeaserContainerStyles = () => css`
  position: relative;

  display: flex;
  align-items: flex-start;

  height: 100%;
  padding: 0;

  background-color: ${colors.grey1};
  border-radius: ${borderRadius.m};

  @media print {
    background-color: transparent !important;
    border-radius: 0;
    margin: ${space.s};
  }

  ${({ hasHorizontalContent }: TeaserContainerProps) =>
    hasHorizontalContent &&
    css`
      flex-direction: row;
      justify-content: space-between;

      ${breakpoint("s", "down")} {
        flex-direction: column;
      }

      > * {
        align-self: stretch;
      }
    `}

  ${({ isInteractive }: TeaserContainerProps) =>
    isInteractive &&
    css`
      ${hoverFocus.defaultFocus}

      transition: background-color ${transition.fast}, box-shadow ${transition.fast};

      &:hover {
        &:not(:disabled) {
          background-color: ${colors.grey2};
          box-shadow: ${boxShadow.l};
        }
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    `}

    @media print {
    margin: 0;
  }
`;

export const TeaserContainer = styled.div<TeaserContainerProps>`
  ${TeaserContainerStyles}

  @media print {
    display: block;
    page-break-inside: avoid;
  }
`;

export const TeaserButtonContainer = styled.button<TeaserContainerProps>`
  ${TeaserContainerStyles}

  text-align: left;

  a {
    pointer-events: none;
  }

  @media print {
    flex-direction: row;

    * {
      border-radius: 0;
    }
  }
`;

export const TeaserContent = styled.div<TeaserContainerProps>`
  align-self: stretch;

  display: flex;
  flex-direction: column;

  flex: 1 1 auto;

  padding: ${space.m};

  text-decoration: none;

  &:focus {
    outline: none;
  }

  > * + * {
    margin-top: ${space.xs};
  }
`;

export const TeaserTitle = styled.span`
  ${fontStyle.bold}
  ${fontSize.base}

  [data-icon] {
    float: left;
    margin-right: ${space.xxs};
  }
`;

export const MapContainer = styled.div`
  border-radius: 0 ${borderRadius.m} ${borderRadius.m} 0;

  width: 150px;
  min-height: 100%;
  height: 100px;

  > * {
    border-radius: inherit;
  }
`;

export const ActionDropdownContainer = styled.div`
  position: absolute;
  margin: ${space.xs};
  right: 0;
  top: 0;
`;

export const DocumentImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;

  padding: ${space.m} ${space.xl} ${space.m} ${space.m};
  margin-left: auto;

  img {
    width: 200px;
    min-height: 200px;
    max-height: 400px;

    object-fit: contain;
  }
`;

export const StyledTravelLegTeaser = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  grid-gap: ${space.s};

  ${breakpoint("s", "down")} {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: repeat(9, 1fr);
    margin: -${space.m} !important;
    background-color: ${colors.grey6};
    padding: ${space.m};
    * {
      text-align: left;
      align-items: flex-start;
    }
  }
`;

export const StyledLightTravelLegTeaser = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${space.xxs};
`;

export const StyledLightTravelTime = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StyledTravelLegInfo = styled.div`
  padding-right: ${space.s};
  border-right: 1px solid ${colors.grey3};

  ${breakpoint("s", "down")} {
    padding-right: 0;
    border: none;

    text-align: center;
  }
`;

export const StyledPersonalTicket = styled.div`
  padding: ${space.m};

  border: 1px solid ${rgba(colors.yellow, 0.5)};
  background-color: ${rgba(colors.yellow, 0.1)};
  border-radius: ${borderRadius.s};

  @media print {
    background-color: ${colors.yellowLight};
    * {
      color: black !important;
    }
  }
`;
